/**
 * Financial Statements — dedicated mapping surface.
 *
 * Pass 1B + follow-up: single source of truth for `fs_mapping` and
 * `note_mapping`. Confirm-before-commit throughout — the searchable
 * `FsCombobox` never writes until the user clicks Apply. AI Suggest and
 * source-hint prefill both route through the same pending-suggestion UI,
 * so no mapping (manual, hint, or AI) is ever silently applied.
 */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useEffect, useMemo, useRef, useState } from "react";
import useEffectEvent from "@/lib/useEffectEvent";   // Step 7c · E3: the effect runs on its own deps only (see the file)
import { PendingMappingPanel } from "@/components/PendingMapping";   // Batch 27 · Add-on 3 · B (M39)
import { pendingCounts } from "@/pages/FsMapping/pendingSuggestions";   // Step 11d-3 · find 5
import { useNavigate, useParams } from "react-router-dom";
import api, { API_URL, toErrMsg } from "@/lib/api";
import { useAICostPreview } from "@/hooks/useAICostPreview";
import { AICostPreview } from "@/components/AICostPreview";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, ArrowLeft, Filter, Search, Sparkles, Loader2,
         Check, X, CheckCircle2, Download, Upload, FileSpreadsheet, Plus } from "lucide-react";
import { toast } from "sonner";
import FsCombobox from "@/components/FsCombobox";
import ReviewQueueBody from "@/pages/FsMapping/ReviewQueueBody";
import { useI18n } from "@/contexts/I18nContext";
import { fmtPlural } from "@/lib/format";
import ImportDialog from "@/pages/FsMapping/ImportDialog";
import AddedByAuditorBadge from "@/components/AddedByAuditorBadge";   // PR 46 review · D1
import { asArray } from "@/lib/asArray";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import CashFlowAccountSettings from "@/components/CashFlowAccountSettings";   // IFRS 18 / CF card V2 — per-account cash-flow settings
import { StatCard } from "@/pages/FsMapping/StatCard";   // STEP 6e split: moved out unchanged
import { NoteCombobox } from "@/pages/FsMapping/NoteCombobox";   // STEP 6e split
import { parseMappingFile } from "@/pages/FsMapping/importParsing";   // STEP 6e split
import { CustomFsDialog, CustomNoteDialog } from "@/pages/FsMapping/CustomCodeDialogs";   // STEP 6e split

export default function FsMapping() {
  const colW = useResizableColumns({ storageKey: "fs-mapping", defaults: [100, 110, 110, 160, 240, 130], mins: [60, 72, 72, 100, 130, 90] });   // Step 11d · add-on
  const { t, lang, dict } = useI18n();
  const { eng_id } = useParams();
  const navigate = useNavigate();
  const [engagement, setEngagement] = useState(null);
  const [tax, setTax] = useState({ fs_lines: [], note_lines: [] });
  const [coa, setCoa] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState({ fs_mapping: "", note_mapping: "" });
  const [savingId, setSavingId] = useState(null);
  const [suggestions, setSuggestions] = useState({});  // FS-mapping suggestions
  const [noteSuggestions, setNoteSuggestions] = useState({});  // Note-mapping suggestions
  const [suggestingBulk, setSuggestingBulk] = useState(false);
  const [suggestingNotesBulk, setSuggestingNotesBulk] = useState(false);
  const [suggestingRow, setSuggestingRow] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFileName, setImportFileName] = useState("");
  const [importRows, setImportRows] = useState([]);
  const [importSummary, setImportSummary] = useState(null);
  const [importApplying, setImportApplying] = useState(false);
  const [customFsOpen, setCustomFsOpen] = useState(false);
  const [customNoteOpen, setCustomNoteOpen] = useState(false);
  const [priorYearOpen, setPriorYearOpen] = useState(false);
  const [noteCoverage, setNoteCoverage] = useState(null);   // Batch 4.2 activation banner
  const [priorCandidates, setPriorCandidates] = useState([]);
  const [priorLoading, setPriorLoading] = useState(false);
  const [priorSource, setPriorSource] = useState("");
  const [priorOverwrite, setPriorOverwrite] = useState(false);
  const [priorApplying, setPriorApplying] = useState(false);
  const [applyingAll, setApplyingAll] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [queueMode, setQueueMode] = useState("fs");         // fs | note
  const [queueSelected, setQueueSelected] = useState(new Set());
  const [queueDismissed, setQueueDismissed] = useState(new Set());
  const [queueSort, setQueueSort] = useState("conf-asc");   // conf-asc | conf-desc
  const [queueApplying, setQueueApplying] = useState(false);
  const [minConfidence, setMinConfidence] = useState(0.7);

  // Persistent dismissals — hydrated from Mongo on mount so partners aren't
  // re-shown suggestions they already rejected. Scoped to (engagement,
  // account, mode) — see backend test_fs_dismissed_isolation.py.
  useEffect(() => {
    if (!eng_id) return;
    api.get(`/fs/${eng_id}/dismissed-suggestions`).then((r) => {
      const ids = new Set(asArray(r.data).map((d) => d.coa_mapping_id));
      setQueueDismissed(ids);
    }).catch(() => {});
  }, [eng_id]);
  const importInputRef = useRef(null);

  // Fix-Now deep-link support — apply URL params on first load and clear them.
  // Supported params:
  //   ?filter_fs=<fs_code>   — pre-filter search to that FS code
  //   ?only=unmapped         — start with the Unmapped filter active
  //   ?focus=<raw_code>      — jump to (and briefly highlight) the CoA row
  //                            whose raw_account_code matches; used by the
  //                            Sampling "Skipped rows" dialog Fix-in-place link.
  const [focusCode, setFocusCode] = useState("");
  const [flashCode, setFlashCode] = useState("");
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fsFilter = params.get("filter_fs");
    const only = params.get("only");
    const focus = params.get("focus");
    if (only === "unmapped") setFilter("unmapped");
    if (fsFilter) {
      setSearch(fsFilter);
      toast.info(t("fsMappingPage.toasts.filteredFs").replace("{code}", fsFilter));
    }
    if (focus) {
      setFocusCode(focus);
      setSearch(focus);
      setFilter("all");
      toast.info(t("fsMappingPage.toasts.jumpedAccount").replace("{code}", focus));
    }
    // eslint-disable-next-line
  }, []);

  // Scroll to and briefly flash the focused row once CoA has hydrated.
  useEffect(() => {
    if (!focusCode || coa.length === 0) return;
    const target = coa.find((c) => (c.raw_account_code || "").trim() === focusCode.trim());
    if (!target) return;
    setFlashCode(target.raw_account_code);
    // Give the DOM a tick to render the filter change before we look for the row.
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="fs-row-${target.id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    // Clear the flash after a few seconds — pure visual cue.
    const clear = setTimeout(() => setFlashCode(""), 4500);
    return () => { clearTimeout(t); clearTimeout(clear); };
  }, [focusCode, coa]);

  const applyReviewQueueSelected = async () => {
    if (queueSelected.size === 0) { toast.error(t("fsMappingPage.toasts.selectAtLeastOne")); return; }
    setQueueApplying(true);
    try {
      const r = await api.post(`/fs/${eng_id}/bulk-apply-suggestions`, {
        mode: queueMode,
        ids: Array.from(queueSelected),
        min_confidence: 0,   // reviewed rows are auditor-approved; no threshold gate
      });
      toast.success(
        t("fsMappingPage.toasts.approvedFromQueue").replace("{n}", fmtPlural(r.data.applied, lang, queueMode === "fs" ? dict.plurals.fsMappings : dict.plurals.noteMappings))
      );
      // Drop applied ids from the appropriate suggestion state.
      if (queueMode === "fs") {
        setSuggestions((prev) => {
          const p = { ...prev };
          queueSelected.forEach((id) => delete p[id]);
          return p;
        });
      } else {
        setNoteSuggestions((prev) => {
          const p = { ...prev };
          queueSelected.forEach((id) => delete p[id]);
          return p;
        });
      }
      setQueueSelected(new Set());
      await load();
    } catch (e) {
      toast.error(toErrMsg(e, t("fsMappingPage.toasts.approveFailed")));
    } finally {
      setQueueApplying(false);
    }
  };

  const dismissReviewQueueSelected = async () => {
    if (queueSelected.size === 0) { toast.error(t("fsMappingPage.toasts.selectAtLeastOneDismiss")); return; }
    const ids = Array.from(queueSelected);
    // Optimistic UI — mark locally, then persist. Rollback on failure.
    setQueueDismissed((prev) => {
      const n = new Set(prev);
      ids.forEach((id) => n.add(id));
      return n;
    });
    try {
      await Promise.all(ids.map((id) => {
        const suggestion = (queueMode === "fs" ? suggestions : noteSuggestions)[id] || {};
        const suggested_code = queueMode === "fs"
          ? (suggestion.fs_mapping || "")
          : (suggestion.note_mapping || "");
        return api.post(`/fs/${eng_id}/dismissed-suggestions`, {
          coa_mapping_id: id, mode: queueMode, suggested_code,
        });
      }));
      setQueueSelected(new Set());
      toast.success(`${fmtPlural(ids.length, lang, dict.plurals.suggestions)} ${t("fsMappingPage.toasts.dismissedSuffix")}`);
    } catch (e) {
      // Rollback the optimistic set.
      setQueueDismissed((prev) => {
        const n = new Set(prev);
        ids.forEach((id) => n.delete(id));
        return n;
      });
      toast.error(toErrMsg(e, t("fsMappingPage.toasts.dismissFailed")));
    }
  };

  const applyAllHighConfidence = async (mode) => {
    setApplyingAll(true);
    try {
      const r = await api.post(`/fs/${eng_id}/bulk-apply-suggestions`, {
        mode, min_confidence: minConfidence,
      });
      toast.success(
        t("fsMappingPage.toasts.appliedAllHigh").replace("{n}", fmtPlural(r.data.applied, lang, mode === "fs" ? dict.plurals.fsMappings : dict.plurals.noteMappings)).replace("{pct}", Math.round(minConfidence * 100)) +
        (r.data.skipped_low_confidence ? " " + t("fsMappingPage.toasts.appliedSkipped").replace("{skipped}", r.data.skipped_low_confidence) : "")
      );
      setSuggestions({});
      setNoteSuggestions({});
      await load();
    } catch (e) {
      toast.error(toErrMsg(e, t("fsMappingPage.toasts.applyAllFailed")));
    } finally {
      setApplyingAll(false);
    }
  };

  const openPriorYearDialog = async () => {
    setPriorYearOpen(true);
    setPriorLoading(true);
    setPriorSource("");
    setPriorOverwrite(false);
    try {
      const r = await api.get(`/coa/${eng_id}/prior-year-candidates`);
      setPriorCandidates(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(toErrMsg(e, t("fsMappingPage.toasts.loadPriorFailed")));
      setPriorCandidates([]);
    } finally {
      setPriorLoading(false);
    }
  };

  const applyPriorYear = async () => {
    if (!priorSource) {
      toast.error(t("fsMappingPage.toasts.pickPriorFirst"));
      return;
    }
    setPriorApplying(true);
    try {
      const r = await api.post(`/coa/${eng_id}/copy-mapping`, {
        source_engagement_id: priorSource,
        overwrite: priorOverwrite,
      });
      toast.success(
        t("fsMappingPage.toasts.copiedPrior")
          .replace("{fy}", r.data.source_financial_year)
          .replace("{rows}", fmtPlural(r.data.updated, lang, dict.plurals.rows))
          .replace("{fs}", r.data.fs_copied)
          .replace("{note}", r.data.note_copied) + " " +
        (r.data.skipped_existing ? t("fsMappingPage.toasts.copiedKept").replace("{n}", r.data.skipped_existing) : "") +
        (r.data.unmatched ? t("fsMappingPage.toasts.copiedUnmatched").replace("{n}", r.data.unmatched) : "")
      );
      setPriorYearOpen(false);
      await load();
    } catch (e) {
      toast.error(toErrMsg(e, t("fsMappingPage.toasts.priorCopyFailed")));
    } finally {
      setPriorApplying(false);
    }
  };

  const load = async () => {
    try {
      const [rEng, rTax, rCoa] = await Promise.all([
        api.get(`/engagements/${eng_id}`),
        api.get("/fs/taxonomy", { params: { engagement_id: eng_id } }),   // Step 11d-7 · item 4: an SME engagement reads the SME names
        api.get(`/coa/${eng_id}`),
      ]);
      setEngagement(rEng.data);
      setTax(rTax.data);
      setCoa(Array.isArray(rCoa.data) ? rCoa.data : []);
    } catch (e) {
      toast.error(toErrMsg(e, t("fsMappingPage.toasts.loadMappingFailed")));
    }
  };
  const loadOnChange = useEffectEvent(load);
  useEffect(() => { loadOnChange(); }, [eng_id, loadOnChange]);

  // Batch 4.2 activation-gap fix — surface untagged / auto-inferred
  // accounts under cash-flow-critical FS codes so partners can review
  // before signing off. Reloads whenever the CoA changes.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/fs/${eng_id}/note-coverage`);
        setNoteCoverage(r.data || null);
      } catch { /* best-effort */ }
    })();
  }, [eng_id, coa.length]);

  // On first load with unmapped accounts, silently pre-fetch hint-only
  // suggestions (no AI cost, no writes). Auditor sees chips they can Apply.
  useEffect(() => {
    if (coa.length === 0) return;
    const anyUnmapped = coa.some((c) => !(c.fs_mapping || "").trim());
    if (!anyUnmapped) return;
    (async () => {
      try {
        const r = await api.post(`/fs/${eng_id}/suggest-mappings`, { use_ai: false });
        setSuggestions((prev) => ({ ...prev, ...(r.data.suggestions || {}) }));
      } catch (e) { /* silent — pre-fetch is best-effort */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coa.length, eng_id]);

  const notesByNo = useMemo(() => {
    const g = {};
    for (const n of tax.note_lines) (g[n.note_no] ||= []).push(n);
    return g;
  }, [tax.note_lines]);

  const stats = useMemo(() => {
    const total = coa.length;
    const unmapped = coa.filter((c) => !(c.fs_mapping || "").trim()).length;
    const noteMissing = coa.filter(
      (c) => (c.fs_mapping || "").trim() && !(c.note_mapping || "").trim(),
    ).length;
    // Step 11d-3 · find 5: one "pending" rule for this card, the Review Queue chip and the queue's list
    const pend = pendingCounts(coa, suggestions, noteSuggestions, queueDismissed);
    return { total, unmapped, noteMissing, mapped: total - unmapped,
              suggestedPending: pend.total };
  }, [coa, suggestions, noteSuggestions, queueDismissed]);

  // Part K: totals feeding the inline cost preview next to each
  // AI Suggest button. Kept named the same as the AICostPreview
  // props for grep clarity.
  const unmappedFsCount = stats.unmapped;
  const unmappedNoteCount = stats.noteMissing;

  // Fetch $/unit rate once for the FS mapping page so the preview
  // renders immediately without waiting on button hover.
  const aiCost = useAICostPreview();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return coa.filter((c) => {
      if (filter === "unmapped" && (c.fs_mapping || "").trim()) return false;
      if (filter === "mapped" && !(c.fs_mapping || "").trim()) return false;
      if (filter === "note_missing"
          && (!(c.fs_mapping || "").trim() || (c.note_mapping || "").trim())) return false;
      if (filter === "has_suggestion" && !suggestions[c.id]) return false;
      if (!q) return true;
      return `${c.raw_account_code || ""} ${c.raw_account_name || ""}`
        .toLowerCase().includes(q);
    });
  }, [coa, filter, search, suggestions]);

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((c) => c.id)));
  };

  const commitMapping = async (row, fs_code) => {
    setSavingId(row.id);
    try {
      await api.patch(`/coa/${row.id}/fs-mapping`, { fs_mapping: fs_code });
      setCoa((prev) => prev.map((c) => c.id === row.id ? { ...c, fs_mapping: fs_code } : c));
      // Once user has acted on the suggestion, drop it from the pending list
      setSuggestions((prev) => { const p = { ...prev }; delete p[row.id]; return p; });
      toast.success(fs_code ? t("fsMappingPage.toasts.mapped").replace("{acc}", row.raw_account_code || row.raw_account_name).replace("{code}", fs_code) : t("fsMappingPage.toasts.clearedMapping"));
    } catch (e) {
      toast.error(toErrMsg(e, t("fsMappingPage.toasts.saveFailed")));
    } finally {
      setSavingId(null);
    }
  };
  const commitNote = async (row, note_code) => {
    setSavingId(row.id);
    try {
      await api.patch(`/coa/${row.id}/fs-mapping`, { note_mapping: note_code });
      setCoa((prev) => prev.map((c) => c.id === row.id ? { ...c, note_mapping: note_code } : c));
    } catch (e) { toast.error(toErrMsg(e, t("fsMappingPage.toasts.saveFailed"))); }
    finally { setSavingId(null); }
  };

  const suggestRow = async (row) => {
    setSuggestingRow(row.id);
    try {
      const r = await api.post(`/fs/${eng_id}/suggest-mappings`,
                                { account_ids: [row.id], use_ai: true });
      const s = (r.data.suggestions || {})[row.id];
      if (s) {
        setSuggestions((prev) => ({ ...prev, [row.id]: s }));
        toast.success(t("fsMappingPage.toasts.suggested").replace("{code}", s.fs_mapping).replace("{pct}", Math.round(s.confidence * 100)).replace("{source}", s.source));
      } else {
        toast.error(t("fsMappingPage.toasts.aiCouldNotSuggest"));
      }
    } catch (e) { toast.error(toErrMsg(e, t("fsMappingPage.toasts.aiSuggestFailed"))); }
    finally { setSuggestingRow(null); }
  };

  const suggestAll = async () => {
    setSuggestingBulk(true);
    try {
      const r = await api.post(`/fs/${eng_id}/suggest-mappings`, { use_ai: true, mode: "fs" });
      const s = r.data.suggestions || {};
      setSuggestions((prev) => ({ ...prev, ...s }));
      const suggested = Object.keys(s).length;
      const considered = r.data.considered || 0;
      const stillUnmapped = (r.data.unsuggested || []).length;
      if (stillUnmapped > 0) {
        toast.warning(
          t("fsMappingPage.toasts.aiPartial").replace("{n}", suggested).replace("{c}", considered).replace("{u}", stillUnmapped),
          { duration: 8000 });
      } else {
        toast.success(t("fsMappingPage.toasts.aiAll").replace("{n}", suggested));
      }
    } catch (e) { toast.error(toErrMsg(e, t("fsMappingPage.toasts.aiSuggestFailed"))); }
    finally { setSuggestingBulk(false); }
  };

  const suggestAllNotes = async () => {
    setSuggestingNotesBulk(true);
    try {
      const r = await api.post(`/fs/${eng_id}/suggest-mappings`, { use_ai: true, mode: "note" });
      const s = r.data.suggestions || {};
      setNoteSuggestions((prev) => ({ ...prev, ...s }));
      const suggested = Object.keys(s).length;
      const considered = r.data.considered || 0;
      const stillUnmapped = (r.data.unsuggested || []).length;
      if (stillUnmapped > 0) {
        toast.warning(
          t("fsMappingPage.toasts.aiNotesPartial").replace("{n}", suggested).replace("{c}", considered).replace("{u}", stillUnmapped),
          { duration: 8000 });
      } else {
        toast.success(t("fsMappingPage.toasts.aiNotesAll").replace("{n}", suggested));
      }
    } catch (e) { toast.error(toErrMsg(e, t("fsMappingPage.toasts.aiNoteFailed"))); }
    finally { setSuggestingNotesBulk(false); }
  };

  const downloadTemplate = async () => {
    try {
      const url = `${API_URL}/fs/${eng_id}/mapping-template`;
      const token = localStorage.getItem("af_token");
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `fs-mapping-template_${engagement?.engagement_name || engagement?.client_name || eng_id}.xlsx`;
      a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(toErrMsg(e, t("fsMappingPage.toasts.templateFailed"))); }
  };

  const onImportFile = async (file) => {
    if (!file) return;
    setImportFileName(file.name);
    try {
      const rows = await parseMappingFile(file);
      setImportRows(rows);
      // Dry-run for validation summary
      const r = await api.post(`/fs/${eng_id}/import-mapping`, { apply: false, rows });
      setImportSummary(r.data);
      setImportOpen(true);
    } catch (e) {
      toast.error(toErrMsg(e, t("fsMappingPage.toasts.parseFailed")));
    }
  };

  const applyImport = async () => {
    if (!importRows.length) return;
    setImportApplying(true);
    try {
      const r = await api.post(`/fs/${eng_id}/import-mapping`,
                                { apply: true, rows: importRows, source_file: importFileName });
      toast.success(t("fsMappingPage.toasts.importApplied").replace("{n}", fmtPlural(r.data.accounts_changed, lang, dict.plurals.accounts)));
      if (r.data.pending_count) toast.warning(lang === "ar" ? r.data.pending_message_ar : r.data.pending_message);   // Batch 27 · Add-on 3 · B
      setImportOpen(false);
      setImportRows([]); setImportSummary(null); setImportFileName("");
      await load();
    } catch (e) { toast.error(toErrMsg(e, t("fsMappingPage.toasts.importApplyFailed"))); }
    finally { setImportApplying(false); }
  };

  const applyAllSuggestions = async () => {
    // Apply every pending suggestion in one bulk write — still initiated by
    // human click, so this stays inside the confirm-before-commit contract.
    // Handles BOTH FS Mapping and Note Mapping pending suggestions (both are
    // surfaced under the same "Apply all N suggestions" affordance to match
    // how `stats.suggestedPending` already counts them together).
    const fsPending = Object.entries(suggestions).filter(
      ([id]) => !coa.find((c) => c.id === id)?.fs_mapping);
    const notePending = Object.entries(noteSuggestions).filter(
      ([id]) => !coa.find((c) => c.id === id)?.note_mapping);
    const total = fsPending.length + notePending.length;
    if (total === 0) { toast.error(t("fsMappingPage.toasts.noPending")); return; }
    if (!await askConfirm(t("fsMappingPage.toasts.applyConfirm").replace("{n}", fmtPlural(total, lang, dict.plurals.suggestions)))) return;
    try {
      let n = 0;
      // Group FS suggestions by code and write in one bulk call per code.
      const byFs = {};
      for (const [id, s] of fsPending) (byFs[s.fs_mapping] ||= []).push(id);
      for (const code of Object.keys(byFs)) {
        await api.post("/coa/bulk-fs-mapping",
                        { mapping_ids: byFs[code], fs_mapping: code });
        n += byFs[code].length;
      }
      // Group Note suggestions by segment code and write in one bulk call per code.
      const byNote = {};
      for (const [id, s] of notePending) (byNote[s.note_mapping] ||= []).push(id);
      for (const code of Object.keys(byNote)) {
        await api.post("/coa/bulk-fs-mapping",
                        { mapping_ids: byNote[code], note_mapping: code });
        n += byNote[code].length;
      }
      toast.success(t("fsMappingPage.toasts.applied").replace("{n}", fmtPlural(n, lang, dict.plurals.suggestions)));
      setSuggestions({});
      setNoteSuggestions({});
      await load();
    } catch (e) { toast.error(toErrMsg(e, t("fsMappingPage.toasts.bulkApplyFailed"))); }
  };

  const clearColumn = async (column) => {
    // Filter-driven scope — mirrors "AI Suggest All (FS Mapping)". Cleared
    // rows are the currently visible set under the search + filter combo.
    const ids = filtered.map((c) => c.id);
    if (ids.length === 0) { toast.error(t("fsMappingPage.toasts.noRowsInView")); return; }
    const label = column === "fs" ? t("fsMappingPage.labels.fs") : t("fsMappingPage.labels.note");
    if (!await askConfirm(t("fsMappingPage.toasts.clearConfirm").replace("{label}", label).replace("{n}", fmtPlural(ids.length, lang, dict.plurals.accounts)))) return;
    try {
      const r = await api.post(`/fs/${eng_id}/clear-mapping`,
                                 { column, mapping_ids: ids });
      toast.success(t("fsMappingPage.toasts.cleared").replace("{label}", label).replace("{n}", fmtPlural(r.data.cleared, lang, dict.plurals.accounts)));
      // Drop pending suggestions for the affected column so the stats card
      // reflects the cleared state immediately (they no longer refer to a
      // meaningful "will overwrite" hint).
      if (column === "fs") setSuggestions({});
      else setNoteSuggestions({});
      await load();
    } catch (e) { toast.error(toErrMsg(e, t("fsMappingPage.toasts.clearFailed").replace("{label}", label))); }
  };

  const applyBulk = async () => {
    if (!bulk.fs_mapping && !bulk.note_mapping) { toast.error(t("fsMappingPage.toasts.pickCodeFirst")); return; }
    if (selected.size === 0) { toast.error(t("fsMappingPage.toasts.selectRowsFirst")); return; }
    try {
      const r = await api.post("/coa/bulk-fs-mapping", {
        mapping_ids: Array.from(selected),
        ...(bulk.fs_mapping ? { fs_mapping: bulk.fs_mapping } : {}),
        ...(bulk.note_mapping ? { note_mapping: bulk.note_mapping } : {}),
      });
      toast.success(t("fsMappingPage.toasts.appliedTo").replace("{n}", fmtPlural(r.data.updated, lang, dict.plurals.rows)));
      setBulkOpen(false); setBulk({ fs_mapping: "", note_mapping: "" });
      setSelected(new Set());
      await load();
    } catch (e) { toast.error(toErrMsg(e, t("fsMappingPage.toasts.bulkApplyFailed"))); }
  };

  if (!engagement) return <div className="p-6 text-sm text-muted-foreground">{t("common.loading")}</div>;
  const allChecked = filtered.length > 0 && selected.size === filtered.length;

  return (
    <div className="space-y-6 p-4" data-testid="fs-mapping-page">
      <PendingMappingPanel engId={eng_id} refreshKey={importSummary ? 1 : 0} />
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Button size="sm" variant="ghost" onClick={() => navigate("/ifrs")}
                  data-testid="fs-mapping-back">
            <ArrowLeft className="me-1 h-3.5 w-3.5" /> {t("fsMappingPage.back")}
          </Button>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">{t("fsMappingPage.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {engagement.engagement_name || engagement.client_name} · FY{engagement.financial_year}
            {engagement.presentation_framework && (
              <span className="ms-2 rounded-md border border-slate-300 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:bg-slate-900/60 dark:text-slate-300"
                    data-testid="fs-mapping-framework">
                {t("fsMappingPage.framework")} <b>{engagement.presentation_framework}</b>
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Status dashboard */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <StatCard label={t("fsMappingPage.stats.total")}      value={stats.total} tone="neutral" testId="fs-stat-total" />
        <StatCard label={t("fsMappingPage.stats.mapped")}     value={stats.mapped}
                  tone="ok" testId="fs-stat-mapped" />
        <StatCard label={t("fsMappingPage.stats.unmapped")}   value={stats.unmapped}
                  tone={stats.unmapped ? "danger" : "ok"} testId="fs-stat-unmapped" />
        <StatCard label={t("fsMappingPage.stats.pending")}    value={stats.suggestedPending}
                  tone={stats.suggestedPending ? "warn" : "neutral"}
                  testId="fs-stat-suggested" />
      </div>

      <CashFlowAccountSettings coa={coa} onSaved={load} />
      {/* Batch 4.2 activation-gap banner — flags accounts under cash-flow-
          critical FS codes that are still untagged or were auto-inferred
          (partner review required). Hidden when coverage is 100 % clean. */}
      {noteCoverage?.totals && (noteCoverage.totals.untagged > 0
          || noteCoverage.totals.auto_inferred > 0) && (
        <Card className={
              noteCoverage.totals.untagged > 0
                ? "border-amber-300 bg-amber-50/60 dark:bg-amber-950/30 dark:border-amber-700"
                : "border-sky-300 bg-sky-50/60 dark:bg-sky-950/30 dark:border-sky-700"}
              data-testid="note-coverage-banner">
          <CardContent className="flex flex-wrap items-start gap-3 py-3 text-sm">
            <AlertTriangle className={`mt-0.5 h-4 w-4 flex-shrink-0 ${
              noteCoverage.totals.untagged > 0 ? "text-amber-600" : "text-sky-600"}`} />
            <div className="flex-1 space-y-1.5">
              <div className="font-medium">
                {t("fsMappingPage.coverage.heading")}{" "}
                {noteCoverage.totals.untagged > 0
                  ? t("fsMappingPage.coverage.needTagging").replace("{n}", fmtPlural(noteCoverage.totals.untagged, lang, dict.plurals.accounts))
                  : t("fsMappingPage.coverage.autoInferred").replace("{n}", fmtPlural(noteCoverage.totals.auto_inferred, lang, dict.plurals.accounts))}
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">
                {t("fsMappingPage.coverage.body").split("{autoBadge}").map((chunk, i, arr) => (
                  <React.Fragment key={i}>
                    {chunk}
                    {i < arr.length - 1 && (
                      <span className="rounded-md bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                        {t("fsMappingPage.coverage.autoBadge")}
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </p>
              <div className="flex flex-wrap gap-2 pt-1 text-[11px]">
                {Object.entries(noteCoverage.by_fs_code || {})
                  .filter(([, b]) => b.untagged > 0 || b.auto_inferred > 0)
                  .sort()
                  .map(([fs, b]) => (
                    <button key={fs}
                            onClick={() => { setSearch(fs); setFilter("note_missing"); }}
                            className="rounded-md border border-slate-300 bg-white px-2 py-0.5 font-mono hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:hover:bg-slate-800"
                            data-testid={`note-coverage-chip-${fs}`}>
                      <span className="font-semibold">{fs}</span>{" "}
                      <span className="text-muted-foreground">
                        {b.tagged}/{b.total}
                      </span>
                      {b.untagged > 0 && (
                        <span className="ms-1 text-amber-700 dark:text-amber-300">
                          {t("fsMappingPage.coverage.untaggedSuffix").replace("{n}", b.untagged)}
                        </span>
                      )}
                      {b.auto_inferred > 0 && (
                        <span className="ms-1 text-sky-700 dark:text-sky-300">
                          {t("fsMappingPage.coverage.autoSuffix").replace("{n}", b.auto_inferred)}
                        </span>
                      )}
                    </button>
                  ))}
              </div>
            </div>
            <Button size="sm" variant="outline"
                    onClick={async () => {
                      try {
                        const r = await api.post(`/fs/${eng_id}/note-inference-refresh`);
                        toast.success(
                          t("fsMappingPage.coverage.reinferredToast")
                            .replace("{n}", r.data.totals.changed)
                            .replace("{left}", r.data.totals.left_untagged)
                        );
                        await load();
                        const rc = await api.get(`/fs/${eng_id}/note-coverage`);
                        setNoteCoverage(rc.data || null);
                      } catch (e) { toast.error(toErrMsg(e, t("fsMappingPage.coverage.reinferFailed"))); }
                    }}
                    className="border-amber-400 text-amber-800 hover:bg-amber-100 dark:text-amber-200"
                    data-testid="note-coverage-refresh">
              {t("fsMappingPage.coverage.rerun")}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Toolbar */}
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">{t("fsMappingPage.accounts")}</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute start-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder={t("fsMappingPage.searchPlaceholder")} className="h-8 w-56 ps-7"
                     data-testid="fs-search" />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="h-8 w-48" data-testid="fs-filter">
                <Filter className="me-1 h-3 w-3" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("fsMappingPage.filters.all")}</SelectItem>
                <SelectItem value="unmapped">{t("fsMappingPage.filters.unmapped")}</SelectItem>
                <SelectItem value="mapped">{t("fsMappingPage.filters.mapped")}</SelectItem>
                <SelectItem value="note_missing">{t("fsMappingPage.filters.noteMissing")}</SelectItem>
                <SelectItem value="has_suggestion">{t("fsMappingPage.filters.hasSuggestion")}</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline"
                    onClick={suggestAll} disabled={suggestingBulk}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300"
                    data-testid="fs-suggest-all">
              {suggestingBulk
                ? <Loader2 className="me-1 h-3 w-3 animate-spin" />
                : <Sparkles className="me-1 h-3 w-3" />}
              {t("fsMappingPage.buttons.suggestAllFs")}
            </Button>
            <AICostPreview scale={0.5} count={unmappedFsCount} unit="accounts" testId="fs-suggest-all-cost" hook={aiCost} />
            <Button size="sm" variant="outline"
                    onClick={suggestAllNotes} disabled={suggestingNotesBulk}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300"
                    data-testid="fs-suggest-notes">
              {suggestingNotesBulk
                ? <Loader2 className="me-1 h-3 w-3 animate-spin" />
                : <Sparkles className="me-1 h-3 w-3" />}
              {t("fsMappingPage.buttons.suggestNotes")}
            </Button>
            <AICostPreview scale={0.5} count={unmappedNoteCount} unit="notes" testId="fs-suggest-notes-cost" hook={aiCost} />
            <Button size="sm" variant="outline"
                    onClick={() => { setQueueOpen(true); setQueueSelected(new Set()); }}
                    className="border-violet-300 text-violet-700 hover:bg-violet-50 dark:text-violet-300"
                    data-testid="fs-review-queue-btn">
              <Sparkles className="me-1 h-3 w-3" /> {t("fsMappingPage.buttons.reviewQueue")}
              {stats.suggestedPending > 0 && (   /* Step 11d-3 · find 5: what is really pending (was every suggestion held) */
                <span className="ms-1 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold text-violet-800 dark:bg-violet-950/50 dark:text-violet-200"
                      data-testid="fs-review-queue-count">
                  {stats.suggestedPending}
                </span>
              )}
            </Button>
            <Button size="sm" variant="outline"
                    onClick={() => applyAllHighConfidence("fs")}
                    disabled={applyingAll}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300"
                    data-testid="fs-apply-all-fs">
              {applyingAll
                ? <Loader2 className="me-1 h-3 w-3 animate-spin" />
                : <CheckCircle2 className="me-1 h-3 w-3" />}
              {t("fsMappingPage.buttons.applyAllFs").replace("{pct}", Math.round(minConfidence * 100))}
            </Button>
            <Button size="sm" variant="outline"
                    onClick={() => applyAllHighConfidence("note")}
                    disabled={applyingAll}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300"
                    data-testid="fs-apply-all-note">
              {applyingAll
                ? <Loader2 className="me-1 h-3 w-3 animate-spin" />
                : <CheckCircle2 className="me-1 h-3 w-3" />}
              {t("fsMappingPage.buttons.applyAllNotes").replace("{pct}", Math.round(minConfidence * 100))}
            </Button>
            <Button size="sm" variant="outline"
                    onClick={downloadTemplate}
                    data-testid="fs-download-template">
              <Download className="me-1 h-3 w-3" /> {t("fsMappingPage.buttons.downloadTemplate")}
            </Button>
            <Button size="sm" variant="outline"
                    onClick={openPriorYearDialog}
                    className="border-teal-300 text-teal-700 hover:bg-teal-50 dark:text-teal-300"
                    data-testid="fs-copy-prior-year-btn">
              <FileSpreadsheet className="me-1 h-3 w-3" /> {t("fsMappingPage.buttons.copyPriorYear")}
            </Button>
            <input ref={importInputRef} type="file"
                   accept=".xlsx,.csv"
                   className="hidden"
                   onChange={(e) => { onImportFile(e.target.files?.[0]); e.target.value = ""; }}
                   data-testid="fs-import-file" />
            <Button size="sm" variant="outline"
                    onClick={() => importInputRef.current?.click()}
                    data-testid="fs-import-btn">
              <Upload className="me-1 h-3 w-3" /> {t("fsMappingPage.buttons.importMapping")}
            </Button>
            <Button size="sm" variant="outline"
                    onClick={() => clearColumn("fs")}
                    disabled={filtered.length === 0}
                    className="border-rose-300 text-rose-700 hover:bg-rose-50 dark:text-rose-300"
                    data-testid="fs-clear-fs-mapping">
              <X className="me-1 h-3 w-3" /> {t("fsMappingPage.buttons.clearFs")}
            </Button>
            <Button size="sm" variant="outline"
                    onClick={() => clearColumn("note")}
                    disabled={filtered.length === 0}
                    className="border-rose-300 text-rose-700 hover:bg-rose-50 dark:text-rose-300"
                    data-testid="fs-clear-note-mapping">
              <X className="me-1 h-3 w-3" /> {t("fsMappingPage.buttons.clearNote")}
            </Button>
            {/* Custom-FS-line launcher — visible to every role at the firm.
                Uses the same firm-scoped 90-99 SCLL slot the taxonomy
                reserves; per-firm, never cross-tenant. */}
            <Button size="sm" variant="outline"
                    onClick={() => setCustomFsOpen(true)}
                    className="border-fuchsia-300 text-fuchsia-800 hover:bg-fuchsia-50 dark:text-fuchsia-200"
                    data-testid="fs-add-custom-fs-btn">
              <Plus className="me-1 h-3 w-3" /> {t("fsMappingPage.buttons.addCustomFs")}
            </Button>
            {stats.suggestedPending > 0 && (
              <Button size="sm"
                      onClick={applyAllSuggestions}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="fs-apply-all-suggestions">
                <Check className="me-1 h-3 w-3" /> {t("fsMappingPage.buttons.applyAllSuggestions").replace("{n}", stats.suggestedPending)}
              </Button>
            )}
            {selected.size > 0 && (
              <Button size="sm" onClick={() => setBulkOpen(true)}
                      variant="outline"
                      data-testid="fs-bulk-apply-btn">
                {t("fsMappingPage.buttons.bulkSet").replace("{n}", selected.size)}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <ResizableTable cols={colW} className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground dark:bg-slate-900/60">
              <tr>
                <ResizableTh index={0} className="w-8 px-2 py-2">
                  <Checkbox checked={allChecked} onCheckedChange={toggleAll}
                            data-testid="fs-select-all" />
                </ResizableTh>
                <ResizableTh index={1} className="px-3 py-2 text-start">{t("fsMappingPage.cols.code")}</ResizableTh>
                <ResizableTh index={2} className="px-3 py-2 text-start">{t("fsMappingPage.cols.account")}</ResizableTh>
                <ResizableTh index={3} className="px-3 py-2 text-start">{t("fsMappingPage.cols.fsMapping")}</ResizableTh>
                <ResizableTh index={4} className="px-3 py-2 text-start">{t("fsMappingPage.cols.noteMapping")}</ResizableTh>
                <ResizableTh index={5} className="px-3 py-2 text-start">{t("fsMappingPage.cols.actions")}</ResizableTh>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                  {coa.length === 0
                    ? t("fsMappingPage.emptyNoAccounts")
                    : t("fsMappingPage.emptyNoMatches")}
                </td></tr>
              )}
              {filtered.map((c) => {
                const unmapped = !(c.fs_mapping || "").trim();
                const noteMissing = !unmapped && !(c.note_mapping || "").trim();
                const isSel = selected.has(c.id);
                const sug = suggestions[c.id];
                const isFlashing = flashCode
                  && (c.raw_account_code || "").trim() === flashCode.trim();
                return (
                  <tr key={c.id}
                      className={
                        `border-b hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors ` +
                        (isFlashing ? "bg-yellow-100 ring-2 ring-yellow-400 dark:bg-yellow-900/30 " :
                         unmapped ? "bg-red-50/40 dark:bg-red-950/10 " :
                         noteMissing ? "bg-amber-50/40 dark:bg-amber-950/10 " : "") +
                        (isSel ? "bg-emerald-50/40 dark:bg-emerald-950/20" : "")
                      }
                      data-testid={`fs-row-${c.id}`}>
                    <td className="px-2 py-2 text-center">
                      <Checkbox checked={isSel} onCheckedChange={() => toggle(c.id)}
                                data-testid={`fs-select-${c.id}`} />
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px]">
                      {c.raw_account_code || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{c.raw_account_name || "—"}</div>
                      {unmapped && (
                        <span className="mt-0.5 inline-flex items-center gap-1 rounded-md bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-800 dark:bg-red-950/40 dark:text-red-300">
                          <AlertTriangle className="h-3 w-3" /> {t("fsMappingPage.unmappedBadge")}
                        </span>
                      )}
                      <AddedByAuditorBadge row={c} />
                      {c.category_hint && (
                        <span className="ms-1 mt-0.5 inline-block rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                          {t("fsMappingPage.hintPrefix").replace("{hint}", c.category_hint)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <FsCombobox
                        value={c.fs_mapping || ""}
                        onCommit={(code) => commitMapping(c, code)}
                        fsLines={tax.fs_lines}
                        suggestion={sug}
                        disabled={savingId === c.id}
                        testId={`fs-picker-${c.id}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <NoteCombobox
                        value={c.note_mapping || ""}
                        onCommit={(code) => commitNote(c, code)}
                        notesByNo={notesByNo}
                        suggestion={noteSuggestions[c.id]}
                        disabled={!c.fs_mapping || savingId === c.id}
                        testId={`note-picker-${c.id}`}
                        onAddCustom={() => setCustomNoteOpen(true)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      {!c.fs_mapping && (
                        <Button size="sm" variant="ghost"
                                onClick={() => suggestRow(c)}
                                disabled={suggestingRow === c.id}
                                className="h-7 text-emerald-700 hover:text-emerald-900 dark:text-emerald-300"
                                data-testid={`fs-suggest-${c.id}`}>
                          {suggestingRow === c.id
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <Sparkles className="h-3 w-3" />}
                          <span className="ms-1">{t("fsMappingPage.aiSuggest")}</span>
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </ResizableTable>
        </CardContent>
      </Card>

      {/* Custom FS + Note code dialogs — visible to every role at the firm.
          Confirm-before-commit is inherent (the dialog itself is the confirm).
          Codes are always auto-assigned in reserved 90-99 / .80-.99 / N80-N89
          slots so a firm-custom code can never collide with an official one. */}
      <CustomFsDialog open={customFsOpen} onOpenChange={setCustomFsOpen}
                       onCreated={load} />
      <CustomNoteDialog open={customNoteOpen} onOpenChange={setCustomNoteOpen}
                         fsLines={tax.fs_lines} onCreated={load} />

      {/* AI Review Queue dialog */}
      <Dialog open={queueOpen} onOpenChange={setQueueOpen}>
        <DialogContent className="max-w-4xl" data-testid="review-queue-dialog">
          <DialogHeader>
            <DialogTitle>
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-violet-600" />
                {t("fsMappingPage.queue.title")}
              </div>
            </DialogTitle>
          </DialogHeader>
          <ReviewQueueBody
            coa={coa}
            fsSuggestions={suggestions}
            noteSuggestions={noteSuggestions}
            dismissed={queueDismissed}
            mode={queueMode}
            onModeChange={(m) => { setQueueMode(m); setQueueSelected(new Set()); }}
            sort={queueSort}
            onSortChange={setQueueSort}
            selected={queueSelected}
            onSelectedChange={setQueueSelected}
            minConfidence={minConfidence}
          />
          <DialogFooter className="justify-between">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <span>{t("fsMappingPage.queue.confGate")}</span>
                <b className="text-slate-700 dark:text-slate-200">
                  {Math.round(minConfidence * 100)}%
                </b>
                <input
                  type="range" min="0" max="100" step="5"
                  value={Math.round(minConfidence * 100)}
                  onChange={(e) => setMinConfidence(Number(e.target.value) / 100)}
                  className="w-32 accent-violet-600"
                  data-testid="review-queue-threshold"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setQueueOpen(false)}
                      data-testid="review-queue-close">{t("fsMappingPage.queue.close")}</Button>
              <Button variant="outline"
                      onClick={dismissReviewQueueSelected}
                      disabled={queueSelected.size === 0}
                      className="border-slate-300"
                      data-testid="review-queue-dismiss-selected">
                <X className="me-1 h-3 w-3" /> {t("fsMappingPage.queue.dismiss").replace("{n}", queueSelected.size)}
              </Button>
              <Button
                onClick={applyReviewQueueSelected}
                disabled={queueSelected.size === 0 || queueApplying}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="review-queue-approve-selected">
                {queueApplying
                  ? <Loader2 className="me-1 h-3 w-3 animate-spin" />
                  : <Check className="me-1 h-3 w-3" />}
                {t("fsMappingPage.queue.approve").replace("{n}", queueSelected.size)}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prior-year Copy dialog */}
      <Dialog open={priorYearOpen} onOpenChange={setPriorYearOpen}>
        <DialogContent className="max-w-lg" data-testid="prior-year-dialog">
          <DialogHeader>
            <DialogTitle>{t("fsMappingPage.prior.title")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t("fsMappingPage.prior.desc").replace("{client}", engagement?.client_name || "")}
            </p>
            {priorLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> {t("fsMappingPage.prior.loading")}
              </div>
            ) : priorCandidates.length === 0 ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
                   data-testid="prior-year-empty">
                {t("fsMappingPage.prior.empty")}
              </div>
            ) : (
              <div className="space-y-1" data-testid="prior-year-candidates">
                {priorCandidates.map((c) => (
                  <label key={c.id}
                         className={`flex cursor-pointer items-center justify-between gap-2 rounded-md border p-2 text-sm ${
                           priorSource === c.id ? "border-teal-400 bg-teal-50 dark:border-teal-700 dark:bg-teal-950/30" : "border-slate-200 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-900/40"
                         }`}
                         data-testid={`prior-year-candidate-${c.id}`}>
                    <div className="flex items-center gap-2">
                      <input type="radio" name="prior-source" value={c.id}
                             checked={priorSource === c.id}
                             onChange={() => setPriorSource(c.id)}
                             data-testid={`prior-year-radio-${c.id}`} />
                      <div>
                        <div className="font-medium">FY{c.financial_year}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {fmtPlural(c.mapped_count, lang, dict.plurals.accounts)}{t("fsMappingPage.prior.mappedSuffix")}
                          {c.presentation_framework ? ` · ${c.presentation_framework}` : ""}
                          {c.status ? ` · ${c.status}` : ""}
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={priorOverwrite}
                        onCheckedChange={(v) => setPriorOverwrite(!!v)}
                        data-testid="prior-year-overwrite" />
              {t("fsMappingPage.prior.overwrite")}
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPriorYearOpen(false)}
                    data-testid="prior-year-cancel">{t("fsMappingPage.prior.cancel")}</Button>
            <Button onClick={applyPriorYear}
                    disabled={!priorSource || priorApplying}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                    data-testid="prior-year-apply">
              {priorApplying
                ? <Loader2 className="me-1 h-3 w-3 animate-spin" />
                : <FileSpreadsheet className="me-1 h-3 w-3" />}
              {t("fsMappingPage.prior.apply")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk-apply dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("fsMappingPage.bulk.title").replace("{n}", fmtPlural(selected.size, lang, dict.plurals.rows))}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("fsMappingPage.bulk.fsCode")}</div>
              <FsCombobox value={bulk.fs_mapping}
                            onCommit={(v) => setBulk((b) => ({ ...b, fs_mapping: v }))}
                            fsLines={tax.fs_lines}
                            testId="fs-bulk-fs-picker" />
              <div className="mt-1 text-[10px] text-muted-foreground">
                {t("fsMappingPage.bulk.fsBlankHint")}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("fsMappingPage.bulk.noteCode")}</div>
              <NoteCombobox value={bulk.note_mapping}
                              onCommit={(v) => setBulk((b) => ({ ...b, note_mapping: v }))}
                              notesByNo={notesByNo}
                              testId="fs-bulk-note-picker" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>{t("fsMappingPage.bulk.cancel")}</Button>
            <Button onClick={applyBulk}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="fs-bulk-apply-confirm">
              {t("fsMappingPage.bulk.apply")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import review dialog — validation summary before commit */}
      <ImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        fileName={importFileName}
        summary={importSummary}
        applying={importApplying}
        onCancel={() => setImportOpen(false)}
        onApply={applyImport}
      />
    </div>
  );
}
